import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { format } from "date-fns";
import clsx from "clsx";
import { apiClient } from "../../services/apiClient";

const MessageBubble = ({
  message,
  isLatest = false,
  isStreaming = false,
  streamingContent = "",
}) => {
  const [showSources, setShowSources] = useState(false);
  const [showReferencedDocs, setShowReferencedDocs] = useState(false);
  const [copied, setCopied] = useState(false);
  const [displayContent, setDisplayContent] = useState("");

  const lastStreamedContentRef = useRef("");

  const isUser = message.message_type === "user";
  const isAI = message.message_type === "assistant";

  // Enhanced streaming content management
  useEffect(() => {
    if (isAI) {
      if (isStreaming && streamingContent) {
        setDisplayContent(streamingContent);
        lastStreamedContentRef.current = streamingContent;
      } else if (!isStreaming) {
        const finalContent =
          message.message_text || lastStreamedContentRef.current;

        if (finalContent !== displayContent) {
          setDisplayContent(finalContent);
        }
      }
    }
  }, [
    isStreaming,
    streamingContent,
    message.message_text,
    isAI,
    message.message_id,
  ]);

  const handleCopyMessage = async () => {
    try {
      const contentToCopy = displayContent || message.message_text;
      await navigator.clipboard.writeText(contentToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy message:", error);
    }
  };

  const formatTime = (timestamp) => {
    try {
      return format(new Date(timestamp), "h:mm a");
    } catch {
      return "";
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return { icon: "file-pdf", color: "text-red-500" };
      case "doc":
      case "docx":
        return { icon: "file-word", color: "text-blue-500" };
      case "xls":
      case "xlsx":
        return { icon: "file-excel", color: "text-emerald-500" };
      case "ppt":
      case "pptx":
        return { icon: "file-powerpoint", color: "text-orange-500" };
      default:
        return { icon: "file-alt", color: "text-slate-500" };
    }
  };

  const renderMarkdown = (content) => {
    return (
      <ReactMarkdown
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            return !inline && match ? (
              <SyntaxHighlighter
                style={oneLight}
                language={match[1]}
                PreTag="div"
                className="rounded-lg text-sm"
                {...props}
              >
                {String(children).replace(/\n$/, "")}
              </SyntaxHighlighter>
            ) : (
              <code
                className="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-sm font-mono"
                {...props}
              >
                {children}
              </code>
            );
          },
          p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
          ul: ({ children }) => (
            <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside mb-3 space-y-1">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="text-slate-700">{children}</li>,
          h1: ({ children }) => (
            <h1 className="text-xl font-semibold mb-3 text-slate-900">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-lg font-semibold mb-2 text-slate-900">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-base font-semibold mb-2 text-slate-900">
              {children}
            </h3>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-slate-300 pl-4 py-2 mb-3 bg-slate-50 rounded-r">
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 underline"
            >
              {children}
              <FontAwesomeIcon
                icon="external-link-alt"
                className="ml-1 text-xs"
              />
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    );
  };

  const StreamingIndicator = () => (
    <div className="flex items-center space-x-2 mt-2 text-slate-500">
      <div className="typing-indicator">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <span className="text-xs">AI is writing...</span>
    </div>
  );

  if (isUser) {
    const hasReferencedDocs =
      message.referenced_document_details &&
      message.referenced_document_details.length > 0;

    return (
      <div className="flex justify-end items-start space-x-3 group">
        <div className="flex flex-col items-end max-w-[70%]">
          <div className="bg-primary-500 text-white rounded-2xl rounded-tr-md px-4 py-3 shadow-sm">
            <p className="text-sm leading-relaxed whitespace-pre-wrap">
              {message.message_text}
            </p>

            {hasReferencedDocs && (
              <div className="mt-3 pt-3 border-t border-primary-400">
                <button
                  onClick={() => setShowReferencedDocs(!showReferencedDocs)}
                  className="flex items-center space-x-2 text-sm text-primary-100 hover:text-white transition-colors duration-200"
                >
                  <FontAwesomeIcon
                    icon={showReferencedDocs ? "chevron-down" : "chevron-right"}
                    className="text-xs"
                  />
                  <FontAwesomeIcon icon="paperclip" className="text-xs" />
                  <span>
                    Referenced {message.referenced_document_details.length}{" "}
                    document
                    {message.referenced_document_details.length !== 1
                      ? "s"
                      : ""}
                  </span>
                </button>

                {showReferencedDocs && (
                  <div className="mt-2 space-y-2">
                    {message.referenced_document_details.map((doc, index) => {
                      const fileIcon = getFileIcon(doc.original_file_name);
                      return (
                        <div
                          key={doc.document_id || index}
                          className="flex items-center space-x-2 p-2 bg-primary-600 bg-opacity-50 border border-primary-400 rounded-lg"
                        >
                          <FontAwesomeIcon
                            icon={fileIcon.icon}
                            className={`text-white text-sm flex-shrink-0`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">
                              {doc.original_file_name}
                            </p>
                            <p className="text-xs text-primary-100">
                              {doc.type_name}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <span className="text-xs text-slate-500">
              {formatTime(message.timestamp)}
            </span>
            <button
              onClick={handleCopyMessage}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors duration-200"
              title="Copy message"
            >
              <FontAwesomeIcon icon={copied ? "check" : "copy"} />
            </button>
          </div>
        </div>
        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
          <FontAwesomeIcon icon="user" className="text-primary-600 text-sm" />
        </div>
      </div>
    );
  }

  if (isAI) {
    const contentToRender = displayContent || "No content";

    return (
      <div className="flex items-start space-x-3 group">
        <div
          className={clsx(
            "h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0",
            {
              "bg-emerald-100": !isStreaming,
              "bg-blue-100": isStreaming,
            }
          )}
        >
          <FontAwesomeIcon
            icon="robot"
            className={clsx("text-sm", {
              "text-emerald-600": !isStreaming,
              "text-blue-600": isStreaming,
            })}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={clsx(
              "border rounded-2xl rounded-tl-md px-4 py-3 shadow-sm",
              {
                "bg-slate-50 border-slate-200": !isStreaming,
                "bg-blue-50 border-blue-200": isStreaming,
              }
            )}
          >
            <div className="prose prose-sm max-w-none">
              {contentToRender ? (
                renderMarkdown(contentToRender)
              ) : isStreaming ? (
                <div className="text-slate-500 italic">AI is thinking...</div>
              ) : (
                <div className="text-slate-500 italic">No content</div>
              )}
            </div>

            {/* Streaming indicator */}
            {isStreaming && <StreamingIndicator />}

            {/* AI Metadata - Only show when not streaming */}
            {!isStreaming && (message.ai_provider || message.model_used) && (
              <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-slate-200">
                <div className="flex items-center space-x-1 text-xs text-slate-500">
                  <FontAwesomeIcon icon="robot" className="text-xs" />
                  <span>
                    {message.ai_provider || "AI"}
                    {message.model_used && ` • ${message.model_used}`}
                  </span>
                </div>
                {message.processing_time_ms && (
                  <div className="text-xs text-slate-400">
                    • {message.processing_time_ms}ms
                  </div>
                )}
              </div>
            )}

            {/* Sources - Only show when not streaming */}
            {!isStreaming &&
              message.source_links &&
              message.source_links.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <button
                    onClick={() => setShowSources(!showSources)}
                    className="flex items-center space-x-2 text-sm text-slate-600 hover:text-slate-800 transition-colors duration-200"
                  >
                    <FontAwesomeIcon
                      icon={showSources ? "chevron-down" : "chevron-right"}
                      className="text-xs"
                    />
                    <span>Sources ({message.source_links.length})</span>
                  </button>

                  {showSources && (
                    <div className="mt-2 space-y-2">
                      {message.source_links.map((source, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2 p-2 bg-white border border-slate-200 rounded-lg"
                        >
                          <FontAwesomeIcon
                            icon="file"
                            className="text-slate-400 text-sm flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {source.file_name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {source.type}{" "}
                              {source.proposal && `• ${source.proposal}`}
                            </p>
                          </div>
                          {source.link && (
                            <button
                              onClick={() =>
                                apiClient.viewDocument(source.document_id)
                              }
                              className="p-1 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                              title="View document"
                            >
                              <FontAwesomeIcon
                                icon="external-link-alt"
                                className="text-xs"
                              />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
          </div>

          {/* Message Actions - Only show when not streaming */}
          {!isStreaming && (
            <div className="flex items-center space-x-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <span className="text-xs text-slate-500">
                {formatTime(message.timestamp)}
              </span>
              <button
                onClick={handleCopyMessage}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors duration-200"
                title="Copy message"
              >
                <FontAwesomeIcon icon={copied ? "check" : "copy"} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default MessageBubble;
